import ReactECharts from 'echarts-for-react';

import data from '../../data/preseedConcentration.json';

/**
 * blog24's figures — THREE interactive ECharts components from ONE committed data file.
 *
 * CHART TYPE FIRST (RULES_BLOG_POST): each figure answers exactly one question, and the type is
 * chosen for that question before any styling. All three are bars, because all three are a
 * before/after comparison of a single quantity across two quarters:
 *
 *   PreseedMoneyChart    the money is flat            $3.22B -> $3.19B
 *   PreseedDealsChart    the deals fell 22%           14,825 -> 11,500
 *   PreseedChequeChart   the average cheque grew 27%  $217.3K -> $276.0K
 *
 * Deliberately NOT done: putting two units on one axis (counts beside dollars), or hand-placing
 * annotation boxes over a bar chart. `CostGapChart` records that exact mistake as the lesson it
 * was built to fix, so each quantity gets its own figure here.
 *
 * RULE 21 — ONE SHARED FORMATTER. `formatFor` is the single place a number becomes a string, and
 * the tooltip, the axis labels and the bar labels all call it. The two states (hover and
 * non-hover) therefore cannot disagree on units, precision or rounding.
 *
 * Mobile-first: the height prop scales by viewport through the caller, labels sit on the bars
 * rather than needing a wide axis, and nothing depends on a legend.
 *
 * Data: `../data/preseedConcentration.json`, the FE twin of the committed source-of-record CSV.
 * No number is typed into this file.
 */

type Row = {
  quarter: string;
  capital_usd_billions: number;
  instruments: number;
  avg_instrument_usd: number;
};
const ROWS = data.rows as Row[];
const [PRIOR, CURRENT] = ROWS;

/** The three value units these figures render. */
type Unit = 'billions' | 'count' | 'thousands';

const BRAND = {
  teal: '#0AB3BF',
  tealLight: '#4DD4E0',
  slate: '#94A3B8',
  axis: '#334155',
  muted: '#64748B',
  amber: '#F59E0B',
};

/**
 * The ONE formatter. Every figure, tooltip, axis tick and bar label goes through it (Rule 21).
 *
 * `role` exists instead of a separate axis lambda so there is exactly one implementation of "a
 * number becomes a string". An axis grid may be coarser than a bar label because a gridline is a
 * scale, not a value; that is a readability choice, and it is still made HERE. An independently
 * written axis formatter is the defect Rule 21 names, because the same figure then renders one
 * number two ways: the axis read `$3.2B` while the tooltip read `$3.19B` before this change.
 */
const formatFor = (unit: Unit, v: number, role: 'label' | 'axis' = 'label'): string => {
  if (unit === 'billions') return role === 'axis' ? `$${v.toFixed(1)}B` : `$${v.toFixed(2)}B`;
  if (unit === 'count') return role === 'axis' ? `${Math.round(v / 1000)}K` : v.toLocaleString('en-US');
  return role === 'axis' ? `$${Math.round(v / 1000)}K` : `$${(v / 1000).toFixed(1)}K`;
};

const baseOption = (
  labels: string[],
  values: number[],
  unit: Unit,
  colour: string,
) => ({
  animationDuration: 600,
  grid: { left: 8, right: 16, top: 28, bottom: 8, containLabel: true },
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    backgroundColor: 'rgba(15,23,42,0.95)',
    borderColor: '#334155',
    textStyle: { color: '#E2E8F0', fontSize: 12 },
    // Same formatter as the labels, so hover and rest can never disagree (Rule 21).
    formatter: (p: Array<{ name: string; value: number }>) =>
      `${p[0]?.name ?? ''}: <strong>${formatFor(unit, p[0]?.value ?? 0)}</strong>`,
  },
  xAxis: {
    type: 'category',
    data: labels,
    axisLine: { lineStyle: { color: BRAND.axis } },
    axisTick: { show: false },
    axisLabel: { color: BRAND.slate, fontSize: 12 },
  },
  yAxis: {
    type: 'value',
    axisLine: { show: false },
    splitLine: { lineStyle: { color: 'rgba(100,116,139,0.18)' } },
    axisLabel: { color: BRAND.muted, fontSize: 11, formatter: (v: number) => formatFor(unit, v, 'axis') },
  },
  series: [
    {
      type: 'bar',
      data: values,
      barMaxWidth: 76,
      itemStyle: { color: colour, borderRadius: [4, 4, 0, 0] },
      // Labels on the bars, so the figure is readable without hovering and without a wide axis.
      label: {
        show: true,
        position: 'top',
        color: '#E2E8F0',
        fontSize: 13,
        fontWeight: 'bold',
        formatter: (p: { value: number }) => formatFor(unit, p.value),
      },
    },
  ],
});

const labels = ROWS.map((r) => r.quarter);

export const PreseedMoneyChart = ({ height = 260 }: { height?: number }) => (
  <ReactECharts
    style={{ height, width: '100%' }}
    option={baseOption(
      labels,
      ROWS.map((r) => r.capital_usd_billions),
      'billions',
      BRAND.teal,
    )}
  />
);

export const PreseedDealsChart = ({ height = 260 }: { height?: number }) => (
  <ReactECharts
    style={{ height, width: '100%' }}
    option={baseOption(
      labels,
      ROWS.map((r) => r.instruments),
      'count',
      BRAND.amber,
    )}
  />
);

export const PreseedChequeChart = ({ height = 260 }: { height?: number }) => (
  <ReactECharts
    style={{ height, width: '100%' }}
    option={baseOption(
      labels,
      ROWS.map((r) => r.avg_instrument_usd),
      'thousands',
      BRAND.tealLight,
    )}
  />
);

/** Derived deltas, computed from the same file so a caption cannot drift from its chart. */
export const deltas = (() => {
  const dCount = ((CURRENT!.instruments - PRIOR!.instruments) / PRIOR!.instruments) * 100;
  const dCheque = ((CURRENT!.avg_instrument_usd - PRIOR!.avg_instrument_usd) / PRIOR!.avg_instrument_usd) * 100;
  const dCapital = ((CURRENT!.capital_usd_billions - PRIOR!.capital_usd_billions) / PRIOR!.capital_usd_billions) * 100;
  return {
    instrumentsPct: Math.abs(dCount).toFixed(0),
    instrumentsAbs: PRIOR!.instruments - CURRENT!.instruments,
    chequePct: Math.abs(dCheque).toFixed(0),
    chequeNow: formatFor('thousands', CURRENT!.avg_instrument_usd),
    capitalPct: Math.abs(dCapital).toFixed(1),
  };
})();
